import { Lightbulb, ShieldAlert, ShieldCheck, Wifi, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import ReportSection from './ReportSection';
import { Finding } from '../CategoryDetailList';
import { tx } from '../../i18n';
import { confidenceCopy, describePercentile } from '../../lib/format';
import {
  benchmarkArea,
  benchmarkProvenance,
  categoryFacts,
  categoryShort,
  categorySubtitle,
  categoryTitle,
  firstSentence,
  isUnrated,
} from '../../lib/report';
import {
  qualityOfCategory,
  qualityReason,
  statusColour,
  statusOfCategory,
} from '../../lib/status';
import type { CategoryDetail, CategoryName, HomeServices } from '../../types/api';

const ICONS: Record<CategoryName, LucideIcon> = {
  SAFETY: ShieldAlert,
  COMFORT: Zap,
  CONNECTIVITY: Wifi,
  COMPLIANCE: ShieldCheck,
};

interface CategorySectionProps {
  category: CategoryDetail;
  services: HomeServices | null | undefined;
  /** When the report read its sources, so every section can say when it was last checked. */
  checkedAt: string;
}

/**
 * One category in full, in its place in the single run of the page: the verdict in a sentence, the
 * facts behind it, the findings with their sources, and what to do next.
 */
export default function CategorySection({ category, services, checkedAt }: CategorySectionProps) {
  const unrated = isUnrated(category);
  const status = statusOfCategory(category);
  const colour = statusColour(status);
  const facts = categoryFacts(category, services);
  const headline = firstSentence(category.summary);
  const rest = category.summary.slice(headline.length).trim();
  const percentile = describePercentile(category.percentile, benchmarkArea(category));
  // Said under the ranking, not inside it: the claim stays short, its provenance stays attached.
  const provenance = percentile ? benchmarkProvenance(category) : null;

  return (
    <ReportSection
      id={`section-${category.category.toLowerCase()}`}
      icon={ICONS[category.category] ?? ShieldCheck}
      title={categoryTitle(category.category)}
      note={categorySubtitle(category.category)}
      status={status}
      quality={qualityOfCategory(category)}
      source={
        category.sourceName
          ? { name: category.sourceName, published: category.sourceDate, checked: checkedAt }
          : { name: tx('Not recorded', 'Kaydedilmedi', 'Nieodnotowane', 'Não registado', 'No registrado'), checked: checkedAt }
      }
      aside={
        <span className="report-section__score" style={{ color: colour }}>
          {unrated ? (
            tx('Unavailable', 'Alınamadı',
    'Niedostępne',
    'Indisponível',
    'No disponible')
          ) : (
            <>
              {category.score}
              <span className="report-section__of">/100</span>
            </>
          )}
        </span>
      }
    >
      <div className="stack stack--tight">
        <h3 className="report-section__headline">{headline}</h3>
        {rest ? <p className="small">{rest}</p> : null}
        <p className="tiny muted">
          {[
            percentile,
            confidenceCopy(category.confidence),
            unrated
              ? null
              : tx(
                  `${category.weightPercent}% of the overall score`,
                  `genel puandaki ağırlığı %${category.weightPercent}`,
    `${category.weightPercent}% oceny ogólnej`,
    `${category.weightPercent}% da pontuação global`,
    `${category.weightPercent}% de la puntuación global`,
                ),
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {provenance ? <p className="tiny muted">{provenance}</p> : null}
        <p className="small muted">{qualityReason(category)}</p>
      </div>

      {facts.length > 0 ? (
        <dl className="fact-grid">
          {facts.map((fact) => (
            <div key={fact.term} className="fact-grid__item">
              <dt>{fact.term}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {category.findings.length > 0 ? (
        <div className="stack stack--tight">
          {category.findings.map((finding) => (
            <Finding
              key={finding.title}
              finding={finding}
              categoryLabel={categoryShort(category.category)}
            />
          ))}
        </div>
      ) : null}

      {category.action ? (
        <div className="next-step">
          <Lightbulb size={16} aria-hidden="true" className="next-step__icon" />
          <div>
            <p className="next-step__title">{tx('What to do next', 'Ne yapmalı',
    'Co zrobić dalej',
    'O que fazer a seguir',
    'Qué hacer a continuación')}</p>
            <p className="small">{category.action}</p>
          </div>
        </div>
      ) : null}

      {/* The source itself is written by the section footer; this is only what it cannot tell you. */}
      {category.limitation ? (
        <p className="small muted">
          <strong>
            {tx('Limitation:', 'Sınırlama:', 'Ograniczenie:', 'Limitação:', 'Limitación:')}
          </strong>{' '}
          {category.limitation}
        </p>
      ) : null}
    </ReportSection>
  );
}
